import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Divider, Form, Image, Modal } from 'semantic-ui-react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/User';
import { StatusContext } from '../context/Status';
import {
  API,
  getCachedStatus,
  getRegistrationAvailability,
  isPasswordLoginEnabled,
  showError,
  showInfo,
  showSuccess,
  showWarning,
} from '../helpers';
import { onGitHubOAuthClicked, onLarkOAuthClicked } from './utils';
import AuthLayout from './AuthLayout';
import larkIcon from '../images/lark.svg';

const LoginForm = () => {
  const { t } = useTranslation();
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
    wechat_verification_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [showWeChatLoginModal, setShowWeChatLoginModal] = useState(false);
  const [searchParams] = useSearchParams();
  const [, userDispatch] = useContext(UserContext);
  const [statusState] = useContext(StatusContext);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || '/channel';

  const status = useMemo(
    () => statusState?.status || getCachedStatus(),
    [statusState?.status]
  );
  const registration = getRegistrationAvailability(status);
  const passwordLoginEnabled = isPasswordLoginEnabled(status);
  const { username, password } = inputs;

  useEffect(() => {
    if (searchParams.get('expired')) {
      showError(t('messages.error.login_expired'));
    }
  }, [searchParams, t]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((current) => ({ ...current, [name]: value }));
  };

  const finishLogin = (data) => {
    userDispatch({ type: 'login', payload: data });
    localStorage.setItem('user', JSON.stringify(data));
    showSuccess(t('messages.success.login'));
    navigate(redirectTo);
  };

  const handleSubmit = async () => {
    if (!passwordLoginEnabled) {
      showInfo(t('auth.login.password_disabled'));
      return;
    }
    if (!username || !password || loading) return;
    setLoading(true);
    try {
      const res = await API.post('/api/user/login', { username, password });
      if (!res?.data) return;
      const { success, message, data } = res.data;
      if (success) {
        finishLogin(data);
        if (username === 'root' && password === '123456') {
          showWarning(t('messages.error.root_password'));
        }
      } else {
        showError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWeChatLogin = async () => {
    if (!inputs.wechat_verification_code || wechatLoading) return;
    setWechatLoading(true);
    try {
      const res = await API.get(
        `/api/oauth/wechat?code=${inputs.wechat_verification_code}`
      );
      if (!res?.data) return;
      const { success, message, data } = res.data;
      if (success) {
        finishLogin(data);
        setShowWeChatLoginModal(false);
      } else {
        showError(message);
      }
    } finally {
      setWechatLoading(false);
    }
  };

  const hasOAuth =
    status.github_oauth || status.wechat_login || status.lark_client_id;

  return (
    <AuthLayout
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
    >
      {passwordLoginEnabled ? (
        <Form size='large' onSubmit={handleSubmit}>
          <Form.Input
            fluid
            icon='user outline'
            iconPosition='left'
            placeholder={t('auth.login.username')}
            name='username'
            value={username}
            onChange={handleChange}
            autoComplete='username'
          />
          <Form.Input
            fluid
            icon='lock'
            iconPosition='left'
            placeholder={t('auth.login.password')}
            name='password'
            type='password'
            value={password}
            onChange={handleChange}
            autoComplete='current-password'
          />
          <Button
            fluid
            type='submit'
            loading={loading}
            disabled={loading || !username || !password}
            className='auth-primary-button'
          >
            <span>{t('auth.login.button')}</span>
            <i className='arrow right icon' aria-hidden='true' />
          </Button>
        </Form>
      ) : (
        <div className='auth-availability-note'>
          {t('auth.login.password_disabled')}
        </div>
      )}

      <div className='auth-links-row'>
        <div className='auth-link-group'>
          <span>{t('auth.login.forgot_password')}</span>
          <Link to='/reset' className='auth-text-link'>
            {t('auth.login.reset_password')}
          </Link>
        </div>
        <div className='auth-link-group'>
          <span>{t('auth.login.no_account')}</span>
          <Link
            to='/register'
            className={`auth-text-link${registration.enabled ? '' : ' is-disabled'}`}
            aria-disabled={!registration.enabled}
            onClick={(event) => {
              if (registration.enabled) return;
              event.preventDefault();
              showInfo(t(`auth.register.disabled_${registration.reason}`));
            }}
          >
            {t('auth.login.register')}
          </Link>
        </div>
      </div>

      {hasOAuth && (
        <>
          <Divider horizontal className='auth-divider'>
            {t('auth.login.other_methods')}
          </Divider>
          <div className='auth-oauth-list'>
            {status.github_oauth && (
              <Button
                type='button'
                icon='github'
                className='auth-oauth-button'
                onClick={() => onGitHubOAuthClicked(status.github_client_id)}
                aria-label='GitHub'
                title='GitHub'
              />
            )}
            {status.wechat_login && (
              <Button
                type='button'
                icon='wechat'
                className='auth-oauth-button'
                onClick={() => setShowWeChatLoginModal(true)}
                aria-label='WeChat'
                title='WeChat'
              />
            )}
            {status.lark_client_id && (
              <Button
                type='button'
                className='auth-oauth-button'
                onClick={() => onLarkOAuthClicked(status.lark_client_id)}
                aria-label='Lark'
                title='Lark'
              >
                <Image src={larkIcon} alt='' />
              </Button>
            )}
          </div>
        </>
      )}

      <Modal
        onClose={() => setShowWeChatLoginModal(false)}
        open={showWeChatLoginModal}
        size='mini'
        closeIcon
      >
        <Modal.Header>{t('auth.login.wechat.title')}</Modal.Header>
        <Modal.Content>
          <Image
            src={status.wechat_qrcode}
            fluid
            alt={t('auth.login.wechat.qr_alt')}
            style={{ borderRadius: 12, marginBottom: 20 }}
          />
          <p
            style={{
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: 22,
              fontSize: 13,
            }}
          >
            {t('auth.login.wechat.scan_tip')}
          </p>
          <Form size='large' onSubmit={handleWeChatLogin}>
            <Form.Input
              fluid
              placeholder={t('auth.login.wechat.code_placeholder')}
              name='wechat_verification_code'
              value={inputs.wechat_verification_code}
              onChange={handleChange}
            />
            <Button
              fluid
              primary
              type='submit'
              loading={wechatLoading}
              disabled={wechatLoading || !inputs.wechat_verification_code}
            >
              {t('auth.login.button')}
            </Button>
          </Form>
        </Modal.Content>
      </Modal>
    </AuthLayout>
  );
};

export default LoginForm;
